/**
 * spanInlineStyleExtension — `<span style>` 의 줄바꿈/공백 관련 inline style 보존용 mark.
 *
 * ### 배경 (라이브러리 한계)
 *
 * `@ngweditor/react@0.2.11` 의 기본 `textStyle` mark 는 `<span>` 의 9개 typography 속성
 * (color / backgroundColor / fontFamily / fontSize / letterSpacing / wordSpacing /
 *  textDecoration / verticalAlign / fontVariant / fontWeight) 만 인식한다. 매칭되는 속성이
 * 하나도 없으면 `getAttrs` 가 `false` 를 반환해 mark 가 적용되지 않고, ProseMirror parser
 * 는 그 `<span>` 자체를 출력에서 제거한다.
 *
 * 외부 메일 본문에 흔히 등장하는
 * `<span style="white-space-collapse: collapse; text-wrap-mode: nowrap;">` 같은 형태가
 * 라운드트립에서 통째로 사라지는 회귀를 본 mark 가 보강한다.
 *
 * ### 화이트리스트
 *
 * 줄바꿈/공백 처리 관련 한정 속성만 보존:
 *   - `text-wrap-mode` — 줄바꿈 모드 (wrap | nowrap | balance ...)
 *   - `white-space` — legacy 줄바꿈/공백 단축 속성 (nowrap | pre | pre-wrap ...)
 *   - `white-space-collapse` — 공백 collapse 동작 (collapse | preserve ...)
 *
 * 새 속성 추가가 필요하면 `WHITELIST` 상수와 `MarkSpec.attrs`, `toDOM` 직렬화 3곳을 동시에
 * 갱신.
 *
 * ### 라이브러리 mark 와의 공존
 *
 * `buildSchemaWithExtensions` 는 같은 이름의 mark 만 덮어쓰므로 신규 이름
 * (`spanInlineStyle`) 으로 등록하면 라이브러리의 `textStyle` mark 와 별개로 공존한다.
 * 한 `<span>` 에 typography 속성과 줄바꿈 속성이 동시에 있으면 두 mark 모두 매칭되어
 * nested span 으로 직렬화되지만 시각 효과는 동일하다.
 *
 * ### 보안
 *
 * 화이트리스트가 줄바꿈 관련 속성만 허용하므로 임의 inline style 주입 경로를 만들지 않는다.
 * 추출 시 brace / url() / javascript: 같은 위험 패턴은 별도 정제 없이 그대로 보관하지만,
 * 화이트리스트 속성 자체가 색/URL/스크립트와 무관한 enum 성 속성이라 XSS 표면 확장이 아니다.
 */

import type { NgwExtension } from '@ngweditor/react'
import type { Mark, MarkSpec } from 'prosemirror-model'

const WHITELIST = ['text-wrap-mode', 'white-space', 'white-space-collapse'] as const
type WhitelistKey = (typeof WHITELIST)[number]

/**
 * raw style 문자열에서 화이트리스트 속성만 추출.
 * 추출 결과가 비었으면 `null` 반환 — caller(getAttrs) 가 `false` 처리.
 */
function extractWhitelisted(styleAttr: string): Partial<Record<WhitelistKey, string>> | null {
  const out: Partial<Record<WhitelistKey, string>> = {}
  for (const entry of styleAttr.split(';')) {
    const trimmed = entry.trim()
    if (trimmed === '') continue
    const colonIdx = trimmed.indexOf(':')
    if (colonIdx <= 0) continue
    const name = trimmed.slice(0, colonIdx).trim().toLowerCase()
    if (!(WHITELIST as readonly string[]).includes(name)) continue
    const value = trimmed.slice(colonIdx + 1).trim()
    if (value === '') continue
    out[name as WhitelistKey] = value
  }
  return Object.keys(out).length === 0 ? null : out
}

const spanInlineStyleMarkSpec: MarkSpec = {
  attrs: {
    textWrapMode: { default: undefined },
    whiteSpace: { default: undefined },
    whiteSpaceCollapse: { default: undefined },
  },
  inclusive: true,
  parseDOM: [
    {
      tag: 'span[style]',
      getAttrs: (dom: HTMLElement | string) => {
        if (typeof dom === 'string') return false
        const styleAttr = dom.getAttribute('style') ?? ''
        const extracted = extractWhitelisted(styleAttr)
        if (extracted == null) return false
        return {
          textWrapMode: extracted['text-wrap-mode'],
          whiteSpace: extracted['white-space'],
          whiteSpaceCollapse: extracted['white-space-collapse'],
        }
      },
    },
  ],
  toDOM(mark: Mark) {
    const attrs = mark.attrs as {
      textWrapMode?: string
      whiteSpace?: string
      whiteSpaceCollapse?: string
    }
    const parts: string[] = []
    if (attrs.textWrapMode != null) parts.push(`text-wrap-mode: ${attrs.textWrapMode}`)
    if (attrs.whiteSpace != null) parts.push(`white-space: ${attrs.whiteSpace}`)
    if (attrs.whiteSpaceCollapse != null) {
      parts.push(`white-space-collapse: ${attrs.whiteSpaceCollapse}`)
    }
    if (parts.length === 0) return ['span', 0]
    return ['span', { style: parts.join('; ') }, 0]
  },
}

export const spanInlineStyleExtension: NgwExtension = {
  name: 'span-inline-style',
  marks: {
    spanInlineStyle: spanInlineStyleMarkSpec,
  },
}
